import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    MAX_COUNT,
    COLOR_PROP_TYPE,
    ICON_PROP_TYPE,
    POSITION_RIGHT,
    POSITION_PROP_TYPE,
} from './constants';
import { getCount } from './utils';
import { Icon } from '@eventbrite/legacy-eds.icon';
import { COLORS_MAP } from '@eventbrite/legacy-eds.styles/color';

import styles from './Badge.module.scss';

const getContent = ({ count, contentColor, iconType, text }) => {
    let content = null;

    if (count) {
        content = getCount(count, MAX_COUNT);
    }
    if (iconType) {
        const iconColor = contentColor || 'white';

        content = <Icon type={iconType} size="xsmall" color={iconColor} isBlockLevel={true} />;
    }
    // Text takes precedence over icon or count
    if (text) {
        content = text;
    }

    return content;
};

/**
 * Helper component that conditionally renders the badge with a number string
 */
const BadgeContent = ({ bgColor, children, contentColor, count, iconType, text, position }) => {
    let component = null;
    const className = classNames(styles.content, {
        [styles.round]: (count >= 0 && count < 10) || iconType || (text && text.length === 1),
        [styles.count]: count,
        [styles.icon]: iconType,
        [styles.standalone]: !iconType && !text && !count,
        [styles.overChildren]: children,
        [styles.overChildrenLeft]: position === 'left' && children,
        [styles.overChildrenRight]: position === 'right' && children,
        [styles.bgColor]: bgColor,
        [styles.textColor]: contentColor,
    });

    // Don't show the badge if count is set to 0
    if (count === 0) {
        return component;
    }

    const content = getContent({ count, contentColor, iconType, text });

    let style = {};
    if (bgColor) {
        style['--badge-background-color'] = COLORS_MAP[bgColor];
    }

    if (contentColor) {
        style['--badge-content-color'] = COLORS_MAP[contentColor];
    }

    component = (
        <div style={style} className={className} data-testid="eds-badge__content">
            {content}
        </div>
    );

    return component;
};

export class Badge extends PureComponent {
    static propTypes = {
        /**
         * The background color of the Badge
         */
        bgColor: COLOR_PROP_TYPE,
        /**
         * The object over which the Badge is placed (e.g. Icon or Text)
         */
        children: PropTypes.node,
        /**
         * The fill color of the text or icon in the Badge
         */
        contentColor: COLOR_PROP_TYPE,
        /**
         * Numerical content of the Badge.
         * Differs from text as it will disappear when count is 0
         * and it will truncate to 99+ when count is > 100
         */
        count: PropTypes.number,
        /**
         * Icon content of the Badge
         */
        iconType: ICON_PROP_TYPE,
        /**
         * Textual content of the Badge
         */
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        /**
         * Position in relation to child node
         */
        position: POSITION_PROP_TYPE,
    };

    static defaultProps = {
        position: POSITION_RIGHT,
    };

    render() {
        const { children } = this.props;

        return (
            <div className={styles.root} data-testid="eds-badge">
                {children}
                <BadgeContent {...this.props} />
            </div>
        );
    }
}
